import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Badge, Spinner, Alert } from "react-bootstrap";
import MapComponent from "../map";
import { FaClock, FaMinus, FaExpand, FaTimes, FaMapMarkerAlt, FaPlay } from "react-icons/fa";
import authAxios from "../../services/Token";

export default function MapaServicioModal({ show, onClose, servicio }) {
  const [timelineMinimized, setTimelineMinimized] = useState(false);
  const [animatingStep, setAnimatingStep] = useState(null);
  const [routeAnimation, setRouteAnimation] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener fecha en formato YYYY-MM-DD
  const getFecha = (servicio) => {
    if (!servicio?.fecha) return null;
    // Si ya viene en formato correcto
    if (/^\d{4}-\d{2}-\d{2}$/.test(servicio.fecha)) return servicio.fecha;
    // Si viene en formato DD/MM/YYYY o similar
    const parts = servicio.fecha.split("-");
    if (parts.length === 3 && parts[0].length === 4) return servicio.fecha; // ya es YYYY-MM-DD
    if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    return servicio.fecha;
  };

  // Helper para obtener el id del conductor de forma robusta
  const getIdConductor = (servicio) => {
    if (!servicio) return null;
    // Intenta varios campos posibles
    return servicio.id_conductor || servicio.conductor || servicio.conductor_id || servicio.id || null;
  };

  // Consumir la API al abrir el modal o cambiar servicio
  useEffect(() => {
    if (!show || !servicio) {
      setTimeline([]);
      setLoading(false);
      setError(null);
      return;
    }
    const idConductor = getIdConductor(servicio);
    const fecha = getFecha(servicio);
    if (!idConductor || !fecha) {
      setTimeline([]);
      setLoading(false);
      setError("No se encontró el ID del conductor o la fecha");
      console.warn("No se encontró el ID del conductor o la fecha", { servicio });
      return;
    }
    setLoading(true);
    setError(null);
    setTimeline([]); // Limpia el timeline anterior
    console.log("Consultando timeline:", { id: idConductor, fecha });
    authAxios.post("exterior/conductor/timeline", { id: idConductor, fecha })
      .then(res => {
        if (res.data && Array.isArray(res.data.timeline)) {
          setTimeline(res.data.timeline);
        } else {
          setTimeline([]);
        }
      })
      .catch(err => {
        setError("Error al cargar el timeline del conductor");
        setTimeline([]);
        console.error("Error al consumir timeline:", err);
      })
      .finally(() => setLoading(false));
    // Limpiar al cerrar
    return () => {
      setTimeline([]);
      setLoading(false);
      setError(null);
    };
  }, [show, servicio]);

  // Limpia animación al abrir/cambiar servicio
  useEffect(() => {
    setAnimatingStep(null);
    setRouteAnimation(null);
  }, [show, servicio]);

  // Normaliza el timeline para asegurar que lat/lng sean números
  const normalizeTimeline = (timelineArr) => {
    return (timelineArr || []).map((step) => ({
      ...step,
      lat: step.lat !== undefined && step.lat !== null && !isNaN(parseFloat(step.lat)) ? parseFloat(step.lat) : null,
      lng: step.lng !== undefined && step.lng !== null && !isNaN(parseFloat(step.lng)) ? parseFloat(step.lng) : null,
    }));
  };

  if (!servicio) return null;

  // Si no hay timeline, usar un mock mínimo para el mapa
  const timelineForMap = normalizeTimeline(
    timeline.length > 0
      ? timeline
      : [{
          id: 1,
          time: servicio.hora_inicial || "08:00",
          action: "Ubicación actual - Disponible",
          lat: parseFloat(servicio.latitud) || 4.6522,
          lng: parseFloat(servicio.longitud) || -74.1059,
          status: "available",
          vehiculo: servicio.vehiculo || 0,
          servicio: servicio.id || null
        }]
  );

  const driver = {
    id: servicio.id,
    name: servicio.conductor_nombre || "Conductor",
    category: "General",
    available: true,
    lat: timelineForMap[0].lat,
    lng: timelineForMap[0].lng,
    vehicle: servicio.vehicle || "Vehículo",
    availableTime: timelineForMap[0].time,
    avatar: `/placeholder.svg?height=40&width=40&text=C`,
    timeline: timelineForMap
  };

  const handleStepClick = (step) => {
    setAnimatingStep(step);
    const currentStepIndex = driver.timeline.findIndex((s) => s.id === step.id);
    const routeSteps = driver.timeline.slice(0, currentStepIndex + 1);
    setRouteAnimation({
      steps: routeSteps,
      currentStep: step,
      driver: driver,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available": return "#10b981";
      case "busy": return "#ef4444";
      case "waiting": return "#f59e0b";
      case "break": return "#6b7280";
      default: return "#6b7280";
    }
  };
  const getStatusText = (status) => {
    switch (status) {
      case "available": return "Disponible";
      case "busy": return "Ocupado";
      case "waiting": return "Esperando";
      case "break": return "Descanso";
      default: return "Desconocido";
    }
  };

  return (
    <Modal show={show} onHide={onClose} dialogClassName="modal-xxl" centered backdrop="static" style={{ zIndex: 2000, padding: 0, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        className="d-flex flex-row justify-content-center align-items-stretch"
        style={{
          minHeight: "80vh",
          maxHeight: "90vh",
          width: "75vw",
          margin: "auto",
          borderRadius: "32px",
          overflow: "hidden",
          boxShadow: "0 8px 40px 0 rgba(16,185,129,0.18)",
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          border: "1.5px solid #e0f2f1",
          position: 'relative',
          left: -270,
          right: 0,
        }}
      >
        {/* Mapa a la izquierda */}
        <div style={{ flex: 2, minWidth: 0, background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", borderRight: "1.5px solid #e0f2f1" }}>
          <MapComponent
            drivers={[driver]}
            onDriverClick={() => {}}
            routeAnimation={routeAnimation}
            initialLocation={{ lat: driver.lat, lng: driver.lng }}
          />
        </div>
        {/* Timeline a la derecha */}
        <div style={{ flex: 1, minWidth: 350, maxWidth: 450, background: "linear-gradient(180deg, #ffffffcc 0%, #f0fdf4cc 100%)", boxShadow: "-4px 0 24px rgba(16,185,129,0.08)", display: 'flex', flexDirection: 'column' }}>
          <Card className="h-100 border-0 rounded-0 shadow-none" style={{ background: "transparent", border: "none" }}>
            <Card.Header className="d-flex align-items-center justify-content-between border-0" style={{ background: "linear-gradient(135deg, #10b981, #059669)", color: "white", borderTopRightRadius: "0", borderTopLeftRadius: "0" }}>
              <div className="d-flex align-items-center">
                <div className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold shadow-sm" style={{ width: "48px", height: "48px", background: "linear-gradient(135deg, #ffffff, #f0fdf4)", color: "#059669" }}>
                  {driver.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <h5 className="mb-0 fw-bold text-white">{driver.name}</h5>
                  <Badge style={{ background: "linear-gradient(135deg, #34d399, #10b981)", color: "white" }}>Servicio #{driver.id}</Badge>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button variant="link" size="sm" onClick={() => setTimelineMinimized(!timelineMinimized)} className="text-white p-0 rounded-circle" style={{ width: "32px", height: "32px" }}>
                  {timelineMinimized ? <FaExpand className="fs-6" /> : <FaMinus className="fs-6" />}
                </Button>
                <Button variant="link" size="sm" onClick={onClose} className="text-white p-0 rounded-circle" style={{ width: "32px", height: "32px" }}>
                  <FaTimes className="fs-6" />
                </Button>
              </div>
            </Card.Header>
            {!timelineMinimized && (
              <Card.Body className="p-4 h-100 d-flex flex-column">
                <div className="mb-4">
                  <h4 className="fw-bold text-dark mb-2 d-flex align-items-center">
                    <FaClock className="me-2" style={{ color: "#10b981" }} />
                    Timeline del día
                  </h4>
                  <p className="text-muted small">Haz clic en cualquier paso para ver la ruta animada</p>
                </div>
                <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  {loading && (
                    <div className="d-flex justify-content-center align-items-center h-100 py-5">
                      <Spinner animation="border" variant="success" style={{ width: 48, height: 48 }} />
                      <span className="ms-3 text-success fw-bold">Cargando información...</span>
                    </div>
                  )}
                  {error && (
                    <Alert variant="danger" className="text-center">{error}</Alert>
                  )}
                  {!loading && !error && timeline.length === 0 && (
                    <div className="text-center text-muted py-5">No hay eventos para este conductor en la fecha seleccionada.</div>
                  )}
                  {!loading && !error && timeline.map((step, index) => (
                    <div
                      key={step.id}
                      className={`position-relative d-flex align-items-start p-3 mb-3 rounded-3 cursor-pointer transition-all hover-shadow-lg ${animatingStep?.id === step.id ? "shadow-lg scale-105" : "shadow-sm"}`}
                      style={{ background: animatingStep?.id === step.id ? "linear-gradient(135deg, #d1fae5, #a7f3d0)" : "linear-gradient(135deg, #ffffff, #f8fafc)", border: animatingStep?.id === step.id ? "2px solid #10b981" : "1px solid #e5e7eb" }}
                      onClick={() => handleStepClick(step)}
                    >
                      {/* Línea del timeline */}
                      {index < timeline.length - 1 && (
                        <div className="position-absolute" style={{ left: "23px", top: "40px", width: "2px", height: "40px", background: "linear-gradient(to bottom, #10b981, #34d399)" }}></div>
                      )}
                      {/* Indicador de estado */}
                      <div className="flex-shrink-0 position-relative">
                        <div className="rounded-circle shadow-sm" style={{ width: "24px", height: "24px", minWidth: "24px", backgroundColor: getStatusColor(step.status), border: "2px solid #ffffff" }}></div>
                        {animatingStep?.id === step.id && (
                          <div className="position-absolute top-0 start-0 rounded-circle animate-pulse" style={{ width: "24px", height: "24px", animation: "pulse 1s infinite", background: "linear-gradient(135deg, #10b981, #34d399)" }}></div>
                        )}
                      </div>
                      {/* Contenido */}
                      <div className="ms-3 flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="fs-5 fw-bold text-dark">{step.time}</span>
                          <Badge style={{ background: "linear-gradient(135deg, #f3f4f6, #e5e7eb)", color: "#374151" }}>{getStatusText(step.status)}</Badge>
                        </div>
                        <p className="text-dark fw-bold mb-2 small">{step.action}</p>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center text-muted small">
                            <FaMapMarkerAlt className="me-1" />
                            <span className="font-monospace">{typeof step.lat === 'number' && typeof step.lng === 'number' ? `${step.lat.toFixed(4)}, ${step.lng.toFixed(4)}` : "-"}</span>
                          </div>
                          {animatingStep?.id === step.id && (
                            <div className="d-flex align-items-center small" style={{ color: "#10b981" }}>
                              <FaPlay className="me-1" />
                              <span className="fw-bold">Animando...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Footer */}
                {!loading && !error && timeline.length > 0 && (
                  <div className="mt-4 pt-4 border-top border-light">
                    <div className="d-flex align-items-center justify-content-between small text-muted p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f0fdf4, #dcfce7)" }}>
                      <span className="fw-bold text-dark">📍 Total: {timeline.length} paradas</span>
                      <span className="fw-bold text-dark">🕐 Último: {timeline[timeline.length - 1].time}</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            )}
            {timelineMinimized && (
              <div className="p-2 d-flex flex-column align-items-center gap-2">
                <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm" style={{ width: "32px", height: "32px", background: "linear-gradient(135deg, #10b981, #059669)" }}>
                  {driver.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div className="text-center">
                  <div className="fw-bold small text-dark">{timeline.length}</div>
                  <div className="text-muted small">paradas</div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Modal>
  );
} 